import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

/**
 * Themed react-day-picker (v10) calendar — heritage crimson selection, gold
 * "today" ring, cream surface. Modifier classes (selected / today / …) land on
 * the day `<td>`, so we target the inner button with `[&>button]`.
 */
export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'relative flex flex-col',
        month: 'space-y-3',
        month_caption: 'flex h-9 items-center justify-center',
        caption_label: 'font-display text-sm font-bold text-ink-900',
        nav: 'absolute inset-x-0 top-0 flex h-9 items-center justify-between px-1',
        button_previous:
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-600 transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-30',
        button_next:
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-600 transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-30',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday: 'flex h-8 w-9 items-center justify-center text-[11px] font-semibold uppercase tracking-wide text-ink-400',
        weeks: 'flex flex-col',
        week: 'mt-1 flex w-full',
        day: 'h-9 w-9 p-0 text-center text-sm',
        day_button:
          'inline-flex h-9 w-9 items-center justify-center rounded-md font-medium text-ink-900 transition-colors hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected:
          '[&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
        today: '[&>button]:ring-2 [&>button]:ring-inset [&>button]:ring-gold-500',
        outside: '[&>button]:opacity-40',
        disabled: '[&>button]:pointer-events-none [&>button]:opacity-30 [&>button]:hover:bg-transparent',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  );
}
